import { useCreateOrgKey } from '@hooks/useOrgKeys';
import { getErrorMessage } from '@utils/errorMessages';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';

interface CreateOrgKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
  onCreated: (apiKey: string) => void;
}

type ExpiryPreset = 'never' | '30' | '90' | '365';

const PRESET_LABELS: Record<ExpiryPreset, string> = {
  never: 'Never',
  '30': '30 days',
  '90': '90 days',
  '365': '1 year',
};

const PRESETS: ExpiryPreset[] = ['never', '30', '90', '365'];

function computeExpiresAt(preset: ExpiryPreset): string | undefined {
  if (preset === 'never') return undefined;
  const days = Number(preset);
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

export function CreateOrgKeyDialog({
  open,
  onOpenChange,
  organizationId,
  organizationName,
  onCreated,
}: CreateOrgKeyDialogProps) {
  const [name, setName] = useState('');
  const [isSandbox, setIsSandbox] = useState(false);
  const [expiryPreset, setExpiryPreset] = useState<ExpiryPreset>('never');

  const createMutation = useCreateOrgKey();

  useEffect(() => {
    if (!open) {
      setName('');
      setIsSandbox(false);
      setExpiryPreset('never');
      createMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    createMutation.mutate(
      {
        organization_id: organizationId,
        name: trimmedName,
        is_sandbox: isSandbox,
        expires_at: computeExpiresAt(expiryPreset),
      },
      {
        onSuccess: data => {
          if (data.api_key) {
            onCreated(data.api_key);
          }
          onOpenChange(false);
        },
        onError: err => {
          toast.error(getErrorMessage(err) || 'Failed to create API key');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
          <DialogDescription>
            For <span className="font-medium text-foreground">{organizationName}</span>. The key is
            shown only once.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="org-key-name">Name</Label>
            <Input
              id="org-key-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. ehr-integration"
              autoFocus
              maxLength={64}
              disabled={createMutation.isPending}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2">
            <div>
              <Label htmlFor="org-key-developer-mode">Developer mode</Label>
              <p className="text-xs text-muted-foreground">
                For testing. Requests from this key run in developer mode.
              </p>
            </div>
            <Switch
              id="org-key-developer-mode"
              checked={isSandbox}
              onCheckedChange={setIsSandbox}
              disabled={createMutation.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label id="org-key-expires-label">Expires</Label>
            <RadioGroup
              aria-labelledby="org-key-expires-label"
              value={expiryPreset}
              onValueChange={value => setExpiryPreset(value as ExpiryPreset)}
              disabled={createMutation.isPending}
              className="flex flex-wrap gap-x-6 gap-y-2"
            >
              {PRESETS.map(preset => (
                <div key={preset} className="flex items-center gap-2">
                  <RadioGroupItem id={`org-key-expires-${preset}`} value={preset} />
                  <Label htmlFor={`org-key-expires-${preset}`} className="font-normal">
                    {PRESET_LABELS[preset]}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
